import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PurchaseGrid from './PurchaseGrid';
import PurchaseModal from './PurchaseModal';
import PurchasesPreview from './PurchasesPreview';
import DeleteConfirmModal from '../../common/DeleteConfirmModal';
import ViewToolbar from '../../common/ViewToolbar';
import SupplierModal from '../suppliers/SupplierModal';
import { useModal } from '../../../hooks/useModal';
import { usePeriodFilter } from '../../../hooks/usePeriodFilter';
import { useDebounce } from '../../../hooks/useDebounce';
import * as purchaseService from '../../../services/purchaseService';
import * as supplierService from '../../../services/supplierService';

/**
 * Self-contained Purchase Orders feature view.
 *
 * Includes:
 *  - The purchase order grid listing
 *  - The purchase order modal (create/edit)
 *  - The purchase order preview sub-view (PDF viewer)
 *  - Draft-restore logic (when returning from preview back to the modal)
 */
export default function PurchasesView({ onImportToBill }) {
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [setup, setSetup] = useState([]);
  const [setup2, setSetup2] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm);
  const [gridApi, setGridApi] = useState(null);

  // Sub-view state: 'grid' (default) or 'preview'
  const [subView, setSubView] = useState('grid');
  const [draftPurchase, setDraftPurchase] = useState(null);
  const [draftToRestore, setDraftToRestore] = useState(null);
  const [createdSupplierId, setCreatedSupplierId] = useState(null);

  const editModal = useModal();
  const deleteModal = useModal();
  const createSupplierModal = useModal();
  const periodFilter = usePeriodFilter();

  const filteredPurchases = useMemo(
    () => periodFilter.filterByPeriod(purchases, 'purchase_order_date'),
    [purchases, periodFilter],
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await purchaseService.fetchPurchases(debouncedSearchTerm);
      setPurchases(result.purchases);
      setSuppliers(result.suppliers);
      setItems(result.items);
      setTaxes(result.taxes);
      setSetup(result.setup);
      setSetup2(result.setup2);
    } catch (err) {
      console.error('Error fetching purchase orders:', err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (formData) => {
    try {
      await purchaseService.savePurchase(formData, editModal.record?.purchase_order_id || null);
      editModal.close();
      fetchData();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    }
  };

  const handleDelete = async () => {
    try {
      await purchaseService.deletePurchase(deleteModal.record.purchase_order_id);
      deleteModal.close();
      fetchData();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const handleCreateSupplier = async (formData) => {
    try {
      const created = await supplierService.createSupplier(formData);
      setSuppliers((prev) => {
        const next = [...(prev || []), created];
        next.sort((a, b) => (a.supplier_name || '').localeCompare(b.supplier_name || ''));
        return next;
      });
      setCreatedSupplierId(created?.supplier_id ?? null);
      createSupplierModal.close();
    } catch (err) {
      alert('Failed to save: ' + (err.message || String(err)));
    }
  };

  // Preview flow: modal -> preview
  const handlePreview = (draft) => {
    setDraftPurchase(draft);
    setSubView('preview');
    editModal.close();
  };

  // Back from preview -> reopen modal with draft data
  const handleBackFromPreview = (draft) => {
    setSubView('grid');
    setDraftToRestore(draft);
    const record = draft?.formData?.purchase_order_id != null
      ? purchases.find((p) => p.purchase_order_id === draft.formData.purchase_order_id) ?? null
      : null;
    editModal.open(record);
  };

  // Preview sub-view
  if (subView === 'preview') {
    return (
      <PurchasesPreview
        draftPurchase={draftPurchase}
        onBack={() => handleBackFromPreview(draftPurchase)}
      />
    );
  }

  // Grid view (default)
  return (
    <>
      <ViewToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onAddNew={() => editModal.open(null)}
        onExportCsv={() => gridApi?.exportToCsv()}
        placeholder="Search purchase orders..."
        addLabel="New Purchase Order"
        addColor="bg-blue-500 hover:bg-blue-600"
        period={periodFilter.period}
        onPeriodChange={periodFilter.onPeriodChange}
        dateFrom={periodFilter.dateFrom}
        onDateFromChange={periodFilter.onDateFromChange}
        dateTo={periodFilter.dateTo}
        onDateToChange={periodFilter.onDateToChange}
      />

      <div className="flex-1 min-h-0">
        {loading ? (
          <div className="flex justify-center mt-10">Loading...</div>
        ) : (
          <PurchaseGrid
            rowData={filteredPurchases}
            onEdit={(r) => editModal.open(r)}
            onDelete={(r) => deleteModal.open(r)}
            onGridReady={setGridApi}
          />
        )}
      </div>

      <PurchaseModal
        isOpen={editModal.isOpen}
        onClose={() => { editModal.close(); setDraftToRestore(null); setCreatedSupplierId(null); }}
        onSubmit={handleSave}
        initialData={editModal.record}
        draftToRestore={draftToRestore}
        onClearDraftRestore={() => setDraftToRestore(null)}
        suppliers={suppliers}
        items={items}
        taxes={taxes}
        setup={setup}
        setup2={setup2}
        onPreview={handlePreview}
        purchases={purchases}
        onImportToBill={onImportToBill}
        onCreateSupplier={() => createSupplierModal.open(null)}
        createdSupplierId={createdSupplierId}
      />

      <SupplierModal
        isOpen={createSupplierModal.isOpen}
        onClose={createSupplierModal.close}
        onSubmit={handleCreateSupplier}
        initialData={createSupplierModal.record}
        title="Add New Supplier"
      />

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleDelete}
        recordName={
          deleteModal.record?.purchase_number
            ? `Purchase Order #${deleteModal.record.purchase_number}`
            : 'this purchase order'
        }
      />
    </>
  );
}
